<template>
    <div :class="{ relative: showExclusionsSidebar }">
        <div
            :class="{
                'exclusions-sidebar-open': showExclusionsSidebar,
                'exclusions-sidebar-closed': !showExclusionsSidebar,
            }"
        >
            <div
                class="grid grid-cols-12 gap-4 pl-0 lg:pr-5 items-center justify-between mb-5 lg:mb-10 relative mt-3"
            >
                <div
                    class="col-span-12 md:col-span-9 grid grid-cols-12 gap-4 items-center"
                >
                    <!-- Left: Title -->
                    <div class="md:mb-4 sm:mb-0 col-span-12 md:col-span-3">
                        <h1
                            class="text-2xl md:text-3xl text-gray-800 font-bold"
                        >
                            My Next Card
                        </h1>
                    </div>
                    <div class="md:ml-8 mb-0 col-span-12 md:col-span-9">
                        <span class="text-xs text-gray-800">
                            The following card rankings are our expert
                            recommendations based on a number of criteria. See
                            how we decide on these rankings for you -
                            <a
                                href="#"
                                @click.prevent="
                                    setShowNextCardMethodModal(true)
                                "
                                class="text-orange text-xs hover:text-orange-400"
                            >
                                My Next Card Methodology
                            </a>
                        </span>
                    </div>
                </div>
                <div
                    class="col-span-12 md:col-span-3 text-right absolute top-0 right-0 md:block"
                >
                    <a
                        href="#"
                        @click.prevent="setShowAffiliateModal(true)"
                        class="text-orange text-xs hover:text-orange-400"
                    >
                        Affiliate Disclosure
                    </a>
                </div>
            </div>

            <div class="my-next-card-wrapper grid grid-cols-12 gap-4">
                <div
                    class="col-span-12 relative flex h-full w-full max-w-xs flex-col overflow-y-auto xl:hidden"
                >
                    <h2 class="sr-only">Filters</h2>

                    <div
                        class="fixed inset-0 bg-black bg-opacity-25 z-40"
                        v-if="showFiltersMenu"
                    ></div>
                    <button
                        type="button"
                        class="inline-flex items-center"
                        @click="toggleFiltersMenu"
                    >
                        <span class="text-base font-medium text-gray-700">
                            Filters
                        </span>
                        <!-- Heroicon name: mini/plus -->
                        <svg
                            class="ml-1 h-5 w-5 flex-shrink-0 text-gray-400"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            aria-hidden="true"
                        >
                            <path
                                d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z"
                            />
                        </svg>
                    </button>
                </div>

                <next-card-filters></next-card-filters>

                <div class="recommended-cards col-span-12 xl:col-span-9">
                    <div class="grid grid-cols-12 gap-4 lg:px-5 py-4">
                        <div class="col-span-12">
                            <ul class="mb-6 flex flex-row text-xs md:text-sm">
                                <li
                                    v-for="(user, i) in users"
                                    class="pr-3 text-orange"
                                    :key="'user-' + i"
                                >
                                    <a
                                        href="#"
                                        @click.prevent="loadNextCards(user)"
                                        class="pill"
                                        :class="{ dark: user.show_user }"
                                    >
                                        {{ user.name }}'s Next Card
                                    </a>
                                </li>
                            </ul>
                            <p class="font-medium text-sm text-gray-400">
                                {{ filteredNextCards.length }} Card
                                Recommendations
                            </p>
                        </div>
                    </div>

                    <div
                        class="container mx-auto max-w-6xl lg:px-4 py-4 grid grid-cols-12 gap-4 relative"
                    >
                        <div
                            class="bg-white shadow rounded-lg col-span-10"
                            v-show="filteredNextCards.length === 0 && loaded"
                        >
                            <div class="p-8">
                                <h3
                                    class="text-lg font-medium leading-6 text-gray-900"
                                >
                                    No Results Found
                                </h3>
                                <div
                                    class="mt-2 max-w-xl text-sm text-gray-500"
                                >
                                    <p class="mb-3">
                                        Sorry, no results match the filters you
                                        have selected.
                                    </p>
                                    <p class="mb-3">
                                        Please adjust your filter criteria, or
                                        clear all filters to start over.
                                    </p>
                                </div>
                                <div class="mt-3 text-sm">
                                    <a
                                        href="#"
                                        @click.prevent="clearFilters"
                                        role="button"
                                        class="font-medium text-orange hover:text-orange-400"
                                    >
                                        Clear All Filters
                                        <span aria-hidden="true">&rarr;</span>
                                    </a>
                                </div>
                            </div>
                        </div>

                        <TopCard
                            :card="topCard"
                            :user="activeUser"
                            v-if="
                                filteredNextCards.length > 0 && pageNumber === 0
                            "
                        />

                        <NextCardCard
                            v-for="(nextCard, n) in paginatedNextCards"
                            :key="'next-card-' + n"
                            :nextCard="nextCard"
                        />
                    </div>

                    <div
                        class="px-4 py-3 mt-10 flex items-center justify-between sm:px-6"
                    >
                        <div
                            class="flex-1 flex items-center justify-center flex-col lg:flex-row lg:justify-between"
                            v-if="pageCount > 1"
                        >
                            <div
                                class="order-2 lg:order-1 mb-10 text-center lg:mb-5 w-full lg:w-auto lg:text-left"
                            >
                                Show
                                <select
                                    class="input-select form-select"
                                    v-model="localPageSize"
                                >
                                    <option value="12">12</option>
                                    <option value="24">24</option>
                                    <option value="36">36</option>
                                </select>
                                per page
                            </div>
                            <div
                                class="order-1 lg:order-2 mb-10 text-center lg:mb-5 w-full lg:w-auto lg:text-left"
                            >
                                <nav
                                    class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                                    aria-label="Pagination"
                                >
                                    <button
                                        type="button"
                                        class="relative inline-flex items-center px-4 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                                        @click.prevent="prevPage"
                                        :disabled="pageNumber === 0"
                                    >
                                        <span class="sr-only">Previous</span>
                                        <i class="fal fa-angle-left"></i>
                                    </button>
                                    <a
                                        v-for="(p, i) in pageCount"
                                        :class="{
                                            'z-10 bg-blue-50 border-blue-500 text-blue-600':
                                                i === pageNumber,
                                            'bg-white border-gray-300 text-gray-500 hover:bg-gray-50':
                                                i !== pageNumber,
                                        }"
                                        @click.prevent="gotoPage(i)"
                                        :key="'page-' + i"
                                        href="#"
                                        class="relative inline-flex items-center px-4 py-2 border text-sm font-medium"
                                    >
                                        {{ p }}
                                    </a>
                                    <button
                                        type="button"
                                        class="relative inline-flex items-center px-4 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                                        @click.prevent="nextPage"
                                        :disabled="pageNumber >= pageCount - 1"
                                    >
                                        <span class="sr-only">Next</span>
                                        <i class="fal fa-angle-right"></i>
                                    </button>
                                </nav>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <LoadingState :loading="loading" />

        <ExclusionsSidebar />
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useAppStore } from '@/store/app';
import { useNextCardStore } from '@/store/nextcard';

import LoadingState from '@/components/ui/loading-state/loading-state.vue';
import NextCardCard from '@/components/next-card/next-card-card.vue';
import NextCardFilters from '@/components/next-card/next-card-filters.vue';
import TopCard from '@/components/next-card/top-card.vue';
import ExclusionsSidebar from '@/components/next-card/exclusions-sidebar.vue';

// Store instances
const appStore = useAppStore();
const nextCardStore = useNextCardStore();

// App Store methods and state
const userSettings = computed(() => appStore.userSettings);
const setShowAffiliateModal = appStore.setShowAffiliateModal;
const setShowNextCardMethodModal = appStore.setShowNextCardMethodModal;

// NextCard Store state
const loading = computed(() => nextCardStore.loading);
const loaded = computed(() => nextCardStore.loaded);
const users = computed(() => nextCardStore.users);
const activeUser = computed(() => nextCardStore.activeUser);
const topCard = computed(() => nextCardStore.topCard);
const nextCards = computed(() => nextCardStore.nextCards);
const pageNumber = computed(() => nextCardStore.pageNumber);
const pageSize = computed(() => nextCardStore.pageSize);
const omittedCards = computed(() => nextCardStore.omittedCards);
const showFiltersMenu = computed(() => nextCardStore.showFiltersMenu);
const filteredNextCards = computed(() => nextCardStore.filteredNextCards);
const paginatedNextCards = computed(() => nextCardStore.paginatedNextCards);
const pageCount = computed(() => nextCardStore.pageCount);
const showExclusionsSidebar = computed(
    () => nextCardStore.showExclusionsSidebar
);
const activeNextCard = computed(() => nextCardStore.activeNextCard);
const offenders = computed(() => nextCardStore.offenders);
const fiveTwentyFourCount = computed(() => nextCardStore.fiveTwentyFourCount);
const showFive24FilterCallout = computed(
    () => nextCardStore.showFive24FilterCallout
);

// NextCard Store methods
const getNextCards = nextCardStore.getNextCards;
const loadNextCards = nextCardStore.loadNextCards;
const getActiveUser = nextCardStore.getActiveUser;
const goodProgramName = nextCardStore.goodProgramName;
const toggleFiltersMenu = nextCardStore.toggleFiltersMenu;
const setExclusionsSidebar = nextCardStore.setExclusionsSidebar;
const setPageSize = nextCardStore.setPageSize;
const clearFilters = nextCardStore.clearFilters;
const setFive24shownCookie = nextCardStore.setFive24shownCookie;
const gotoPage = nextCardStore.gotoPage;
const nextPage = nextCardStore.nextPage;
const prevPage = nextCardStore.prevPage;

// Computed properties
const localPageSize = computed({
    get: () => pageSize.value,
    set: (val) => setPageSize(val),
});

// Methods
const nextCardNumber = (nextCard) => {
    return filteredNextCards.value.indexOf(nextCard) + 2;
};

// Watchers
watch(pageNumber, () => {
    let element = document.querySelector('#f-body');
    element.scrollTop = 0;
});

// Lifecycle hooks
onMounted(() => {
    getNextCards();
});
</script>

<style lang="scss" scoped>
.exclusions-sidebar-open {
    transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    transform: translateX(-448px);
}
.exclusions-sidebar-closed {
    transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    transform: translateX(0);
}
label {
    &.disabled {
        opacity: 0.45;
    }
}
</style>
